import { Component, Injector, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import { MatDatepicker } from '@angular/material/datepicker'
import { MatDialog } from '@angular/material/dialog'
import { Router } from '@angular/router'
import { AbstractComponent, Attendance, AttendanceService, AttendanceStatusEnum, Channel, ChannelService, ConfirmationComponent, DateUtil, Department, DepartmentService, ExportService, MessageService, Pager, ReportService, Tag, TagService, Team, TeamService, User, UserService } from 'lib-trend-core'
import { concatMap, debounceTime, distinctUntilChanged, Observable, Subject, switchMap, takeUntil } from 'rxjs'
import { AttendanceHistoryComponent, AttendanceHistoryData, } from '../../attendance/attendance-history/attendance-history.component'
import { FormControl } from '@angular/forms'

export type filterType = {
  status?: string
  department?: string
  channel?: string
  user?: string
  period?: string,
  tabulations?: string
}

@Component({
    selector: 'app-report-attendance-list',
    templateUrl: './report-attendance-list.v2.component.html',
    // templateUrl: './report-attendance-list.component.html',
    styleUrl: './report-attendance-list.v2.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ReportAttendanceListComponent extends AbstractComponent implements OnInit {

  pager: Pager<Attendance> = new Pager<Attendance>({ perPage: 10 });

  @ViewChild('picker', { static: false }) picker: MatDatepicker<Date>;
  isPicker: boolean = false;

  public attendancesPending: number = 0;
  public attendancesInProgress: number = 0;
  public attendancesClosed: number = 0;
  public attendancesOvertime: number = 0;
  public attendancesPaused: number = 0;

  public listObservable: Observable<Pager<Attendance>>;
  private termOfSearch: Subject<string> = new Subject<string>();

  public listStatusAttendances: Array<AttendanceStatusEnum> =
    new Array<AttendanceStatusEnum>(
      AttendanceStatusEnum.OVERTIME,
      AttendanceStatusEnum.PENDING,
      AttendanceStatusEnum.IN_PROGRESS,
      AttendanceStatusEnum.PAUSED,
      AttendanceStatusEnum.CLOSED
    );

  public listDepartment: Array<Department> = new Array<Department>();
  public listChannel: Array<Channel> = new Array<Channel>();
  public listUser: Array<User> = new Array<User>();
  public listTeam: Array<Team> = new Array<Team>();
  public listTeamChannel: Array<Team> = new Array<Team>();
  public listDepartmentChannel: Array<Department> = new Array<Department>();
  public attendantsByTeam: Array<any> = new Array<any>();

  public labelStatus: string = this.t('report.attendanceList.labelStatus');
  public labelDepartment: string = this.t('report.attendanceList.labelDepartment');
  public labelChannel: string = this.t('report.attendanceList.labelChannel');
  public labelUser: string = this.t('report.attendanceList.labelUser');
  public labelPeriod: string = '';

  public isLoading: boolean = false;

  public selectedFilters: filterType = {};

  public attendanceStatusEnum = AttendanceStatusEnum;

  filterControlTags: FormControl = new FormControl('');
  listTag: Array<Tag> = new Array<Tag>();
  allTags: Array<Tag> = new Array<Tag>();
  selectedTagsId: Array<string> | null = new Array<string>();

  breadcrumb = [
    { href: '/report', label: this.t('report.attendanceList.breadcrumbReports') },
    { label: this.t('report.attendanceList.breadcrumbAttendanceReport') },
  ];

  constructor(
    injectore: Injector,
    public userService: UserService,
    public departmentService: DepartmentService,
    public channelService: ChannelService,
    public reportService: ReportService,
    public exportService: ExportService,
    public attendanceService: AttendanceService,
    public messageService: MessageService,
    public teamService: TeamService,
    private tagService: TagService,
    public override router: Router,
    public dialog: MatDialog
  ) {
    super(injectore);
    this.loadingContent = true;
    this.createForm();
  }

  ngOnInit(): void {
    this.initializeData();
  }

  private initializeData(): void {
    this.getAttendancesList();
    this.getAttendancesByStatus(true);
    this.getListChannel();
    this.getTags();


    if (this.isAdmin()) {
      this.getListDepartment();
      this.getListUser();
    } else if (this.isSupervisor() || this.isAttendant()) {
      this.getListTeam();
    }
  }

  private createForm(): void {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);

    this.formGroup = this.formBuilder.group({
      channel: [null],
      user: [null],
      dateRange: this.formBuilder.group({
        dateStart: [thirtyDaysAgo],
        dateEnd: [today],
      }),
      department: [null],
      status: [null],
      tags: [[]]
    });

    this.labelPeriod = `${DateUtil.toFormart(thirtyDaysAgo, 'dd/MM/yyyy')} - ${DateUtil.toFormart(today, 'dd/MM/yyyy')}`;

    let previousValue = this.formGroup.value;

    this.formGroup.valueChanges
      .pipe(debounceTime(100))
      .subscribe((currentValue) => {
        if (
          previousValue.status &&
          (previousValue.channel !== currentValue.channel ||
            previousValue.user !== currentValue.user ||
            previousValue.dateRange.dateStart !== currentValue.dateRange.dateStart ||
            previousValue.dateRange.dateEnd !== currentValue.dateRange.dateEnd ||
            previousValue.department !== currentValue.department)
        ) {
          this.formGroup.patchValue({ status: null }, { emitEvent: false });
        }

        if (
          previousValue.dateRange.dateStart !== currentValue.dateRange.dateStart ||
          previousValue.dateRange.dateEnd !== currentValue.dateRange.dateEnd
        ) {
          this.pager.page = 1;
        }

        this.getAttendancesList();

        if (previousValue.status !== currentValue.status) {
          previousValue = currentValue;
          return;
        }

        this.getAttendancesByStatus(false);
        previousValue = currentValue;
      });
  }

  private getAttendancesList(): void {
    this.loadingContent = true;

    const formValues = this.formGroup.getRawValue();
    const filters: Record<string, unknown> = {
      status: formValues.status,
      department: formValues.department,
      channel: formValues.channel,
      user: formValues.user,
      tags: formValues.tags?.length ? formValues.tags : null,
      ...(formValues.dateRange?.dateStart && formValues.dateRange?.dateEnd
        ? {
          dateStart: formValues.dateRange.dateStart.getTime(),
          dateEnd: formValues.dateRange.dateEnd.getTime(),
        }
        : {}),
    };

    this.searchParams = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as Record<string, unknown>);

    this.attendanceService.getReportAttendances(this.pager.page, this.pager.perPage, this.searchString, this.searchParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (pager: Pager<Attendance>) => {
          this.pager = pager;
          this.setupObservableSearch();
        },
        complete: () => (this.loadingContent = false),
        error: (err) =>
          this.alertService.error(err.error?.message ?? this.t('report.attendanceList.unexpectedError')),
      });
  }

  private getAttendancesByStatus(isFirstTime: boolean = false): void {
    this.attendanceService.getReportAttendancesCounts(this.searchString, this.searchParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (aggResult: Array<{ status: AttendanceStatusEnum; count: number }>) => {

          if (!isFirstTime) {
            this.attendancesPending = 0;
            this.attendancesInProgress = 0;
            this.attendancesClosed = 0;
            this.attendancesOvertime = 0;
            this.attendancesPaused = 0;
          }

          aggResult.forEach(({ status, count }) => {
            switch (status) {
              case AttendanceStatusEnum.PENDING:
                this.attendancesPending = count;
                break;
              case AttendanceStatusEnum.IN_PROGRESS:
                this.attendancesInProgress = count;
                break;
              case AttendanceStatusEnum.OVERTIME:
                this.attendancesOvertime = count;
                break;
              case AttendanceStatusEnum.CLOSED:
                this.attendancesClosed = count;
                break;
              case AttendanceStatusEnum.PAUSED:
                this.attendancesPaused = count;
                break;
              default:
                break;
            }
          });
        },
      });
  }

  searchStatus(status: AttendanceStatusEnum): void {
    const currentStatus = this.formGroup.get('status').value;

    if (currentStatus === status) {
      this.labelStatus = this.t('report.attendanceList.labelStatus');
      this.formGroup.get('status').setValue(null);
    } else {
      this.labelStatus = this.getTranslatedStatusAttendance(status);
      this.formGroup.get('status').setValue(status);
      this.selectedFilters = { ...this.selectedFilters, status: this.labelStatus };
    }
  }

  private getListChannel(): void {
    this.channelService.getList().subscribe({
      next: (list: Array<Channel>) => {
        this.listChannel = list
      },
    })
  }

  private getListDepartment(): void {
    this.departmentService.getList().subscribe({
      next: (list: Array<Department>) => {
        this.listDepartment = list;
      },
    })
  }

  openPicker(): void {
    this.isPicker = true;
    setTimeout(() => {
      this.picker.open();
    }, 300);
  }

  private getListUser(): void {
    this.userService.getList().subscribe({
      next: (list: Array<User>) => {
        this.listUser = list;
      },
    })
  }

  private getListTeam(): void {
    this.teamService.getList()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (list: Array<Team>) => {
          this.listTeam = list
            .filter((team: Team) => {
              const userId = this.getCurrentUserUser()._id;
              const isAgent = team.agents.some(
                (agent: User) => agent._id === userId
              )
              const isSupervisor = team.supervisors.some(
                (supervisor: User) => supervisor._id === userId
              )
              return isAgent || isSupervisor;
            })
            .filter(
              (team: Team) => team.department && team.department.removed !== true
            )
        },
      })
  }

  private setupObservableSearch(): void {
    this.listObservable = this.termOfSearch.pipe(
      takeUntil(this.destroy$),
      debounceTime(500),
      distinctUntilChanged(),
      switchMap((term) => {
        return this.attendanceService.getReportAttendances(this.pager.page, this.pager.perPage, term, this.searchParams);
      })
    )
    this.listObservable.subscribe({
      next: (pager: Pager<Attendance>) => {
        this.pager = pager;
      },
      complete: () => (this.loadingContent = false),
      error: (err) => this.alertService.error(err.error.message),
    })
  }

  search(term: string) {
    this.searchString = term;
    this.termOfSearch.next(term);
  }

  searchByStatus(status: AttendanceStatusEnum): void {
    if (status) {
      this.labelStatus = this.getTranslatedStatusAttendance(status);
      this.formGroup.get('status').setValue(status);
      this.selectedFilters.status = this.labelStatus;
    } else {
      this.labelStatus = this.t('report.attendanceList.labelStatus');
      this.formGroup.get('status').setValue(null);
    }
  }

  searchByDepartment(department: Department): void {
    if (department) {
      this.labelDepartment = department.name;
      this.formGroup.get('department').setValue(department._id);
      if (this.isAdmin()) {
        this.teamService.getList().pipe(takeUntil(this.destroy$)).subscribe((teams: Array<Team>) => {
          this.attendantsByTeam = teams
            .filter((team: Team) => team.department._id === department._id && team.agents?.length)
            .flatMap((team: Team) => team.agents);
        });
      } else if ((this.isSupervisor() || this.isAttendant())) {
        this.attendantsByTeam = this.listTeam
          .filter((team: Team) => team.department._id === department?._id && team.agents?.length)
          .flatMap((team: Team) => team.agents);
      }


    } else {
      this.labelDepartment = this.t('report.attendanceList.labelDepartment');
      this.formGroup.get('department').setValue(null);
    }

    this.selectedFilters.department = this.labelDepartment !== this.t('report.attendanceList.labelDepartment')
      ? this.labelDepartment
      : undefined;

    if (!this.selectedFilters.department) {
      delete this.selectedFilters.department;
    }
  }

  searchByChannel(channel: Channel): void {
    if (channel) {
      this.labelChannel = channel.name;
      this.formGroup.get('channel').setValue(channel._id);

      if (this.isSupervisor()) {
        // supervisor: departments come from the teams the supervisor belongs to
        this.listTeamChannel = (this.listTeam ?? [])
          .filter((team: Team) => team.department?.channel?.toString() === channel._id);
        this.listDepartmentChannel = this.listTeamChannel
          .map((team: Team) => team.department)
          .filter((dept: Department, index: number, self: Array<Department>) =>
            !!dept && self.findIndex((d: Department) => d?._id === dept._id) === index);
      } else {
        this.listDepartmentChannel = (this.listDepartment ?? [])
          .filter((dept: Department) => dept.channel?.toString() === channel._id);
      }
    } else {
      this.labelChannel = this.t('report.attendanceList.labelChannel');
      this.formGroup.get('channel').setValue(null);
      this.listDepartmentChannel = [];
      this.listTeamChannel = [];
    }

    this.selectedFilters.channel = this.labelChannel !== this.t('report.attendanceList.labelChannel')
      ? this.labelChannel
      : undefined;

    if (!this.selectedFilters.channel) {
      delete this.selectedFilters.channel;
    }
    this.pager.page = 1;
  }

  searchByUser(user: User): void {
    if (user) {
      this.labelUser = user.name;
      this.formGroup.get('user').setValue(user._id);
    } else {
      this.labelUser = this.t('report.attendanceList.labelUser');
      this.formGroup.get('user').setValue(null);
    }

    this.selectedFilters.user = this.labelUser !== this.t('report.attendanceList.labelUser')
      ? this.labelUser
      : undefined;

    if (!this.selectedFilters.user) {
      delete this.selectedFilters.user;
    }
  }

  selectTag(event: Event, id: string): void {
    const inputElement = event.target as HTMLInputElement;
    const tagsControl = this.formGroup.get('tags');
    const currentTags = tagsControl?.value || [];

    if (inputElement.checked) {
      tagsControl?.setValue([...currentTags, id]);
    } else {
      tagsControl?.setValue(currentTags.filter((tagId: string) => tagId !== id));
    }
  }

  onFocusTags(): void {
    this.allTags = this.listTag;
  }

  private fetchFilterTags(): void {
    this.filterControlTags.valueChanges.pipe(debounceTime(300)).subscribe((value: string) => {
      const currentValue = this.selectedTagsId || [];

      this.allTags = this.listTag.filter(tag =>
        tag.title.toLowerCase().includes(value.toLowerCase())
      );

      currentValue.forEach(tagId => {
        const existingTag = this.listTag.find(tag => tag._id === tagId);
        if (existingTag && !this.allTags.includes(existingTag)) {
          this.allTags.push(existingTag);
        }
      });
    });
  }

  getTags() {
    this.tagService.getList().pipe(takeUntil(this.destroy$)).subscribe({
      next: (value) => {
        this.listTag = value;
        this.allTags = value;
        this.fetchFilterTags();
      },
    })
  }

  openModalAttendance(data: Attendance): void {
    this.dialog.open(AttendanceHistoryComponent, {
      width: '72vw',
      height: '95vh',
      data: <AttendanceHistoryData>{
        openAttendanceHistoryUnique: true,
        idContact: data.contact._id,
        idAttendance: data._id,
      },
    });
  }

  loadPage(page: number) {
    this.pager.page = page;
    this.getAttendancesList();
  }

  getStarRating(rating: number): Array<string> {
    const stars = Array(5).fill('mdi mdi-star-outline');
    for (let i = 0; i < rating; i++) {
      stars[i] = 'mdi mdi-star';
    }
    return stars;
  }

  calculateDuration(startDate: Date, endDate: Date): string {
    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();
    const duration = end - start;

    const days = Math.floor(duration / (1000 * 60 * 60 * 24));
    const hours = Math.floor((duration / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((duration / (1000 * 60)) % 60);

    if (days > 0) {
      return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
    }

    return `${hours}h ${minutes}m`;
  }

  getAttendanceDuration(attendance: Attendance): string {
    if (
      attendance.status === AttendanceStatusEnum.CLOSED ||
      attendance.status === AttendanceStatusEnum.OVERTIME
    ) {
      if (attendance.closedAt) {
        return this.calculateDuration(attendance.createdAt, attendance.closedAt);
      }
    }
    return '-';
  }

  exportAttendanceReportToExcel(): void {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '500px',
      data: this.t('report.attendanceList.confirmExportExcel'),
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.isLoading = true;
        this.fetchAttendancesRecursively([], new Pager<Attendance>({ perPage: 500 }))
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (attendances: Array<Attendance>) => {
              const formattedData = this.formatAttendanceData(attendances);
              this.exportService
                .generateExcelAttendanceReport(formattedData, this.selectedFilters)
                .then((buffer: any) => this.downloadExcelFile(buffer))
                .catch((err: any) => this.handleError(err))
                .finally(() => {
                  this.alertService.success(this.t('report.attendanceList.excelSuccess'));
                  this.isLoading = false;
                });
            },
          });
      }
    });
  }

  exportAttendanceReportToPdf(): void {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '500px',
      data: this.t('report.attendanceList.confirmExportPdf'),
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.isLoading = true;
        this.fetchAttendancesRecursively([], new Pager<Attendance>({ perPage: 500 }))
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (attendances: Array<Attendance>) => {
              const formattedData = this.formatAttendanceData(attendances);
              this.exportService
                .generatePDFAttendanceReport(formattedData, this.selectedFilters)
                .then(() => this.alertService.success(this.t('report.attendanceList.pdfSuccess')))
                .catch((err: any) => this.handleError(err))
                .finally(() => {
                  this.isLoading = false;
                });
            },
          });
      }
    });
  }

  private fetchAttendancesRecursively(attendances: Attendance[], pager: Pager<Attendance>): Observable<Array<Attendance>> {

    return this.attendanceService.getReportAttendances(pager.page, pager.perPage, this.searchString, this.searchParams)
      .pipe(
        takeUntil(this.destroy$),
        concatMap(newPager => {
          const allAttendances = [...attendances, ...newPager.list];
          if (allAttendances.length >= newPager.total) {
            return new Observable<Array<Attendance>>(observer => {
              observer.next(allAttendances);
            });
          } else {
            return this.fetchAttendancesRecursively(
              allAttendances,
              { ...pager, page: pager.page + 1 }
            );
          }
        })
      );
  }

  private formatAttendanceData(attendances: Attendance[]): any[] {
    return attendances.map((attendance: Attendance) => ({
      protocol: attendance.protocol,
      contact: attendance.contact,
      channel: attendance?.channel,
      department: attendance.department,
      user: attendance?.user,
      status: super.getTranslatedStatusAttendance(attendance.status),
      tags: attendance?.tags?.map((tag: Tag) => tag.title).join(', ') || '-',
      feedback: attendance?.feedbackCSAT,
      feedbackCSAT: this.getStarRating(attendance?.feedbackCSAT ?? 0)
        .reduce(
          (acc: string, star: string) =>
            acc + (star === 'star' ? '⭐' : '☆') + ' ',
          ''
        )
        .trim(),
      duration: this.getAttendanceDuration(attendance),
      createdAt: DateUtil.toFormart(attendance.createdAt, 'dd/MM/yyyy HH:mm:ss'),
      closedAt:
        attendance.status === AttendanceStatusEnum.CLOSED ||
          attendance.status === AttendanceStatusEnum.OVERTIME
          ? DateUtil.toFormart(
            attendance.closedAt ?? attendance.updatedAt,
            'dd/MM/yyyy HH:mm:ss'
          )
          : '-',
    }));
  }

  private downloadExcelFile(buffer: any): void {
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Trendmulti_Relatório_Atendimentos_${new Date().toLocaleDateString()}.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  onPeriodChange(event: any) {
    const today = new Date();
    const period = parseInt(event.value, 10);

    if (!isNaN(period)) {
      this.formGroup.get('dateRange')?.patchValue({
        dateStart: new Date(today.setDate(today.getDate() - period)),
        dateEnd: new Date(),
      });
      this.picker?.close();
    }
  }

  onPickerClose() {
    const dateStart = this.formGroup.get('dateRange')?.get('dateStart')?.value;
    const dateEnd = this.formGroup.get('dateRange')?.get('dateEnd')?.value;

    if (dateStart && dateEnd) {
      this.selectedFilters.period = this.t('report.attendanceList.periodRange', { start: DateUtil.toFormart(dateStart, 'dd/MM/yyyy'), end: DateUtil.toFormart(dateEnd, 'dd/MM/yyyy') });
    } else {
      this.selectedFilters.period = '';
    }
  }

  private handleError(err: any): void {
    this.alertService.error(err.error?.message ?? this.t('report.attendanceList.unexpectedError'));
  }

  translateStatus(status: AttendanceStatusEnum): string {
    switch (status) {
      case AttendanceStatusEnum.IN_PROGRESS:
        return this.t('report.attendanceList.badgeInProgress');
      case AttendanceStatusEnum.PENDING:
        return this.t('report.attendanceList.badgePending');
      case AttendanceStatusEnum.OVERTIME:
        return this.t('report.attendanceList.badgeOvertime');
      case AttendanceStatusEnum.PAUSED:
        return this.t('report.attendanceList.badgePaused');
      case AttendanceStatusEnum.CLOSED:
        return this.t('report.attendanceList.badgeClosed');
      default:
        return this.t('report.attendanceList.badgeUnknown');
    }
  }

}
